import type {
  BasicChartData,
  ChartDataUnion,
  ChartType,
  CreateChartPayload,
  CreateDashboardPayload,
  CubeDataPoint,
  CubeMetaResponse,
  CubeQueryConfig,
  CubeQueryResponse,
  Dashboard,
  GaugeData,
  MetricData,
  SQLQueryConfig,
  TableData,
  UpdateChartPayload,
  UpdateChartPositionsPayload,
  UpdateDashboardPayload,
} from "@web-app/types";
import http from "@web-app/utils/http";
import { getBrowserTimezone } from "@web-app/utils/time";

// ============================================================================
// Date Formatting Helper
// ============================================================================

/**
 * Format an ISO date string from a Cube.js time-dimension bucket into a label that
 * matches the bucket's granularity. Without granularity awareness a monthly bucket
 * ("2026-05-01") reads like a single day and an hourly bucket reads like a date.
 *
 * @param dateString - ISO date string (e.g., "2026-05-01T00:00:00.000")
 * @param granularity - Cube.js granularity (hour/day/week/month/quarter/year)
 */
function formatDateLabel(dateString: string, granularity?: string): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original if invalid date
    }

    let options: Intl.DateTimeFormatOptions;
    switch (granularity) {
      case "hour":
        // e.g. "May 24, 1 PM"
        options = { day: "numeric", hour: "numeric", month: "short" };
        break;
      case "week":
        // e.g. "Week of May 24"
        return `Week of ${date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        })}`;
      case "month":
        // e.g. "May 2026"
        options = { month: "short", year: "numeric" };
        break;
      case "quarter":
        // e.g. "Q2 2026"
        return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
      case "year":
        options = { year: "numeric" };
        break;
      case "day":
      default:
        // e.g. "May 24, 2026"
        options = { day: "numeric", month: "short", year: "numeric" };
        break;
    }

    return date.toLocaleDateString("en-US", options);
  } catch {
    return dateString; // Return original if formatting fails
  }
}

/**
 * Check if a string looks like an ISO date
 */
function isISODate(str: string): boolean {
  // Check for ISO 8601 format patterns
  return /^\d{4}-\d{2}-\d{2}/.test(str);
}

/**
 * Fallback label to use when a dimension value is missing. Region dimensions
 * (e.g. "orders.state", "orders.dropoff_state", "orders.pickup_state") use
 * "N/A" since a missing region isn't unexpected; everything else uses
 * "Unknown".
 */
function missingLabelFallback(dimensionKey: string): string {
  const key = dimensionKey.split(".").pop()?.toLowerCase() || "";
  return key.endsWith("state") || key.includes("region") ? "N/A" : "Unknown";
}

/**
 * Resolve a dimension value to a display label, falling back only when the
 * value is truly missing (null/undefined/empty string) so that valid falsy
 * values like 0 or false render as "0"/"false" rather than "Unknown"/"N/A".
 */
function resolveLabel(value: unknown, dimensionKey: string): string {
  return value === null || value === undefined || value === ""
    ? missingLabelFallback(dimensionKey)
    : String(value);
}

// ============================================================================
// Dashboard CRUD Operations
// ============================================================================

export async function fetchDashboards(): Promise<Dashboard[]> {
  const response = await http.get<Dashboard[]>("/dashboards");
  return response.data;
}

export async function fetchDashboard(id: string): Promise<Dashboard> {
  const response = await http.get<Dashboard>(`/dashboards/${id}`);
  return response.data;
}

export async function createDashboard(
  payload: CreateDashboardPayload,
): Promise<Dashboard> {
  const response = await http.post<Dashboard>("/dashboards", payload);
  return response.data;
}

export async function updateDashboard(
  id: string,
  payload: UpdateDashboardPayload,
): Promise<Dashboard> {
  const response = await http.put<Dashboard>(`/dashboards/${id}`, payload);
  return response.data;
}

export async function deleteDashboard(id: string): Promise<void> {
  await http.delete(`/dashboards/${id}`);
}

// ============================================================================
// Chart Operations
// ============================================================================

export async function addChart(
  dashboardId: string,
  payload: CreateChartPayload,
): Promise<Dashboard> {
  const response = await http.post<Dashboard>(
    `/dashboards/${dashboardId}/charts`,
    payload,
  );
  return response.data;
}

export async function updateChart(
  dashboardId: string,
  chartId: string,
  payload: UpdateChartPayload,
): Promise<Dashboard> {
  const response = await http.put<Dashboard>(
    `/dashboards/${dashboardId}/charts/${chartId}`,
    payload,
  );
  return response.data;
}

export async function deleteChart(
  dashboardId: string,
  chartId: string,
): Promise<Dashboard> {
  const response = await http.delete<Dashboard>(
    `/dashboards/${dashboardId}/charts/${chartId}`,
  );
  return response.data;
}

export async function updateChartPositions(
  dashboardId: string,
  payload: UpdateChartPositionsPayload,
): Promise<Dashboard> {
  const response = await http.put<Dashboard>(
    `/dashboards/${dashboardId}/charts/positions`,
    payload,
  );
  return response.data;
}

// ============================================================================
// Analytics Query Execution
// ============================================================================

export async function executeAnalyticsQuery(
  query: CubeQueryConfig,
): Promise<CubeQueryResponse> {
  const response = await http.post<CubeQueryResponse>("/analytics/query", {
    ...query,
    timezone: getBrowserTimezone(),
  });
  return response.data;
}

export async function getAnalyticsMeta(): Promise<CubeMetaResponse> {
  const response = await http.get<CubeMetaResponse>("/analytics/meta");
  return response.data;
}

export async function executeSQLQuery(
  sqlQuery: SQLQueryConfig,
): Promise<CubeQueryResponse> {
  const response = await http.post<CubeQueryResponse>("/analytics/sql", {
    dateFrom: sqlQuery.dateFrom,
    dateTo: sqlQuery.dateTo,
    query: sqlQuery.sql,
    timezone: getBrowserTimezone(),
  });
  return response.data;
}

// ============================================================================
// Data Transformation Functions
// ============================================================================

/**
 * Transform Cube.js response to Metric chart format
 * Handles both single value and grouped metrics (multiple rows)
 */
export function transformToMetric(cubeData: CubeDataPoint[]): MetricData {
  if (!cubeData || cubeData.length === 0) {
    return {
      format: "number",
      value: 0,
    };
  }

  const firstRow = cubeData[0];

  // Detect if this is grouped data (has dimension + measure)
  const keys = Object.keys(firstRow);

  // Find measure key by checking ALL rows, not just first (first row might have null)
  const measureKey = keys.find((key) => {
    return cubeData.some((row) => {
      const value = row[key];
      return !isNaN(Number(value)) && value !== null && value !== "";
    });
  });

  const dimensionKey = keys.find((key) => {
    const value = firstRow[key];
    return isNaN(Number(value)) || value === null || value === "";
  });

  // If we have multiple rows OR a dimension key, this is grouped data
  if (cubeData.length > 1 || dimensionKey) {
    const metrics = cubeData.map((row) => {
      const rawValue = measureKey ? row[measureKey] : null;
      return {
        label: dimensionKey
          ? (() => {
              const val = resolveLabel(row[dimensionKey], dimensionKey);
              return isISODate(val) ? formatDateLabel(val) : val;
            })()
          : "Total",
        // Convert null to 0 for display (e.g., Dropoff in Pickup OTIF)
        value:
          rawValue !== null && rawValue !== undefined ? Number(rawValue) : 0,
      };
    });

    return {
      format: "number",
      metrics,
      value: 0, // Not used for grouped metrics
    };
  }

  // Single metric value
  const value = measureKey ? Number(firstRow[measureKey]) || 0 : 0;

  return {
    format: "number",
    value,
  };
}

/**
 * Transform Cube.js response to Gauge format
 * If multiple rows, sums all non-null values
 */
export function transformToGauge(cubeData: CubeDataPoint[]): GaugeData {
  if (!cubeData || cubeData.length === 0) {
    return {
      format: "number",
      max: 100,
      value: 0,
    };
  }

  const keys = Object.keys(cubeData[0]);

  // Find measure key by checking ALL rows for numeric values
  const measureKey = keys.find((key) => {
    return cubeData.some((row) => {
      const value = row[key];
      return !isNaN(Number(value)) && value !== null && value !== "";
    });
  });

  if (!measureKey) {
    return {
      format: "number",
      max: 100,
      value: 0,
    };
  }

  // Collect all non-null numeric values
  const values = cubeData
    .map((row) => {
      const val = row[measureKey];
      return val !== null && val !== undefined && !isNaN(Number(val))
        ? Number(val)
        : null;
    })
    .filter((val): val is number => val !== null);

  if (values.length === 0) {
    return {
      format: "number",
      max: 100,
      value: 0,
    };
  }

  const total = values.reduce((sum, val) => sum + val, 0);

  // Detect format and unit from measure key
  const { format, unit, value } = detectGaugeFormat(measureKey, total);

  // Auto-scale max to a nice round number above the value
  const rawMax = Math.max(value * 1.2, 100);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const max = Math.ceil(rawMax / magnitude) * magnitude;

  return {
    format,
    max,
    unit,
    value,
  };
}

function detectGaugeFormat(
  measureKey: string,
  total: number,
): { format: string; unit: string; value: number } {
  const key = measureKey.toLowerCase();

  // Percentage rates (0-100)
  if (
    key.includes("_rate") ||
    key.includes("completion_rate") ||
    key.includes("otif")
  ) {
    return { format: "percentage", unit: "%", value: total };
  }

  // Distance (stored in miles, display as km)
  if (key.includes("distance")) {
    return { format: "number", unit: "km", value: total };
  }

  // Duration (stored in seconds, display as mins)
  if (key.includes("duration") || key.includes("service_time")) {
    return { format: "number", unit: "mins", value: Math.round(total / 60) };
  }

  // Revenue / monetary
  if (
    key.includes("revenue") ||
    key.includes("_value") ||
    key.includes("_tip")
  ) {
    return { format: "currency", unit: "$", value: total };
  }

  // Counts and items — no unit
  return { format: "number", unit: "", value: total };
}

/**
 * Transform Cube.js response to Basic Chart format (bar, line, area, column)
 * Also handles SQL query results by inferring structure from column names
 */
export function transformToBasicChart(
  cubeData: CubeDataPoint[],
  query: CubeQueryConfig | null,
  columnOrder?: string[],
  columnLabels?: string[],
): BasicChartData {
  if (!cubeData || cubeData.length === 0) {
    return { datasets: [], labels: [] };
  }

  // If no query config (SQL mode), infer from data structure
  if (!query) {
    const firstRow = cubeData[0];
    // Use columnOrder if provided to maintain SELECT clause order
    const usingColumnOrder = !!columnOrder && columnOrder.length > 0;
    const keys = usingColumnOrder ? columnOrder : Object.keys(firstRow);
    // SQL alias (AS) for a key, if the query gave one - columnLabels is only
    // meaningful in lockstep with columnOrder, not the Object.keys fallback.
    const aliasFor = (key: string): string | undefined => {
      if (!usingColumnOrder || !columnLabels) return undefined;
      return columnLabels[columnOrder.indexOf(key)] || undefined;
    };

    // Detect which columns are numeric (measures) vs string (dimensions)
    // Check all rows since the first row may have null for a numeric column
    const measureKeys: string[] = [];
    const dimensionKeys: string[] = [];

    keys.forEach((key) => {
      const isNumeric = cubeData.some((row) => {
        const value = row[key];
        return value !== null && value !== "" && !isNaN(Number(value));
      });
      if (isNumeric) {
        measureKeys.push(key);
      } else {
        dimensionKeys.push(key);
      }
    });

    // Use first dimension as labels, or first key if no dimensions found
    const labelKey = dimensionKeys[0] || keys[0];
    // Use detected measures, or remaining keys if none found
    const dataKeys =
      measureKeys.length > 0 ? measureKeys : keys.filter((k) => k !== labelKey);

    const labels = cubeData.map((row) => {
      const value = resolveLabel(row[labelKey], labelKey);
      return isISODate(value) ? formatDateLabel(value) : value;
    });
    const datasets = dataKeys.map((measureKey) => {
      const data = cubeData.map((row) => Number(row[measureKey]) || 0);
      // Remove "orders." prefix and format the label
      const cleanKey = measureKey.replace(/^orders\./, "");
      return {
        data,
        label:
          aliasFor(measureKey) ||
          cleanKey.charAt(0).toUpperCase() +
            cleanKey.slice(1).replace(/_/g, " "),
      };
    });

    return {
      axisLabels: {
        x:
          aliasFor(labelKey) ||
          labelKey
            .replace(/^orders\./, "")
            .charAt(0)
            .toUpperCase() +
            labelKey
              .replace(/^orders\./, "")
              .slice(1)
              .replace(/_/g, " "),
        y: datasets[0]?.label || "Value",
      },
      datasets,
      labels,
    };
  }

  // Cube.js mode
  // Check if we have a time dimension (with granularity) or regular dimension
  let dimensionKey: string;
  let isTimeDimension = false;

  // Only use time dimension if it has granularity (used for grouping, not just filtering)
  const timeDimensionWithGranularity = query.timeDimensions?.find(
    (td) => td.granularity,
  );

  if (timeDimensionWithGranularity) {
    // Use time dimension for labels (e.g., "by month", "by day")
    dimensionKey = timeDimensionWithGranularity.dimension;
    isTimeDimension = true;
  } else if (query.dimensions && query.dimensions.length > 0) {
    // Use regular dimension for labels (e.g., "by workspace", "by assignee")
    dimensionKey = query.dimensions[0];
  } else {
    // No dimension, return empty
    return { datasets: [], labels: [] };
  }

  const measureKeys = query.measures;

  // Extract unique labels from dimension and format dates if needed
  const labels = cubeData.map((row) => {
    const value = resolveLabel(row[dimensionKey], dimensionKey);
    // Format date labels for time dimensions, honoring the bucket granularity
    return isTimeDimension && isISODate(value)
      ? formatDateLabel(value, timeDimensionWithGranularity?.granularity)
      : value;
  });

  // Create datasets for each measure
  const datasets = measureKeys.map((measureKey) => {
    const data = cubeData.map((row) => Number(row[measureKey]) || 0);
    const label = measureKey.split(".").pop() || measureKey;

    return {
      data,
      label: label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, " "),
    };
  });

  // Generate axis labels
  const xAxisLabel = isTimeDimension
    ? "Date"
    : dimensionKey.split(".").pop()?.replace(/_/g, " ") || "Category";
  const yAxisLabel =
    datasets[0]?.label || measureKeys[0]?.split(".").pop() || "Value";

  return {
    axisLabels: {
      x: xAxisLabel.charAt(0).toUpperCase() + xAxisLabel.slice(1),
      y: yAxisLabel,
    },
    datasets,
    labels,
  };
}

/**
 * Transform Cube.js response to Pie/Donut chart format
 * Also handles SQL query results by inferring structure from data types
 *
 * Supports two data structures:
 * 1. Multiple rows with dimension + measure: each row becomes a pie slice
 *    e.g., [{status: "Complete", count: 10}, {status: "Failed", count: 5}]
 * 2. Single row with multiple measures: each measure becomes a pie slice
 *    e.g., [{pickup_otif_rate: 69.17, pickup_late_rate: 30.82}]
 */
export function transformToPieChart(
  cubeData: CubeDataPoint[],
  query: CubeQueryConfig | null,
  columnOrder?: string[],
  columnLabels?: string[],
): BasicChartData {
  if (!cubeData || cubeData.length === 0) {
    return { datasets: [], labels: [] };
  }

  // If no query config (SQL mode), infer from data structure
  if (!query) {
    const firstRow = cubeData[0];
    // Use columnOrder if provided to maintain SELECT clause order
    const usingColumnOrder = !!columnOrder && columnOrder.length > 0;
    const keys = usingColumnOrder ? columnOrder : Object.keys(firstRow);
    // SQL alias (AS) for a key, if the query gave one - columnLabels is only
    // meaningful in lockstep with columnOrder, not the Object.keys fallback.
    const aliasFor = (key: string): string | undefined => {
      if (!usingColumnOrder || !columnLabels) return undefined;
      return columnLabels[columnOrder.indexOf(key)] || undefined;
    };

    // Detect which columns are numeric (measures) vs string (dimensions)
    const measureKeys: string[] = [];
    const dimensionKeys: string[] = [];

    keys.forEach((key) => {
      const value = firstRow[key];
      const numValue = Number(value);
      if (!isNaN(numValue) && value !== null && value !== "") {
        measureKeys.push(key);
      } else {
        dimensionKeys.push(key);
      }
    });

    // Special case: Single row with multiple measures (measures-only query)
    // Each measure becomes a slice in the pie chart
    if (
      cubeData.length === 1 &&
      measureKeys.length > 1 &&
      dimensionKeys.length === 0
    ) {
      const labels = measureKeys.map((key) => {
        if (aliasFor(key)) return aliasFor(key) as string;
        // Format: remove "orders." prefix, replace underscores, capitalize
        const cleanKey = key.replace(/^orders\./, "");
        return cleanKey
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      });
      const data = measureKeys.map((key) => Number(firstRow[key]) || 0);

      return {
        datasets: [
          {
            data,
            label: "Values",
          },
        ],
        labels,
      };
    }

    // Standard case: Multiple rows with dimension + measure
    const labelKey = dimensionKeys[0] || keys[0];
    const measureKey = measureKeys[0] || keys[1];

    const labels = cubeData.map((row) => {
      const value = resolveLabel(row[labelKey], labelKey);
      return isISODate(value) ? formatDateLabel(value) : value;
    });
    const data = cubeData.map((row) => Number(row[measureKey]) || 0);

    return {
      datasets: [
        {
          data,
          label:
            aliasFor(measureKey) ||
            measureKey.charAt(0).toUpperCase() + measureKey.slice(1),
        },
      ],
      labels,
    };
  }

  // Cube.js mode
  // Check if we have a time dimension (with granularity) or regular dimension
  let dimensionKey: string;
  let isTimeDimension = false;

  // Only use time dimension if it has granularity (used for grouping, not just filtering)
  const timeDimensionWithGranularity = query.timeDimensions?.find(
    (td) => td.granularity,
  );

  if (timeDimensionWithGranularity) {
    dimensionKey = timeDimensionWithGranularity.dimension;
    isTimeDimension = true;
  } else if (query.dimensions && query.dimensions.length > 0) {
    dimensionKey = query.dimensions[0];
  } else {
    return { datasets: [], labels: [] };
  }

  const measureKey = query.measures[0];

  const labels = cubeData.map((row) => {
    const value = resolveLabel(row[dimensionKey], dimensionKey);
    // Format date labels for time dimensions, honoring the bucket granularity
    return isTimeDimension && isISODate(value)
      ? formatDateLabel(value, timeDimensionWithGranularity?.granularity)
      : value;
  });
  const data = cubeData.map((row) => Number(row[measureKey]) || 0);

  return {
    datasets: [
      {
        data,
        label: measureKey.split(".").pop()?.replace(/_/g, " ") || measureKey,
      },
    ],
    labels,
  };
}

/**
 * Transform Cube.js response to Table format
 * Also handles SQL query results by inferring from column names
 * @param columnOrder - Optional array of column keys in the order they should appear (from SQL SELECT)
 */
export function transformToTable(
  cubeData: CubeDataPoint[],
  query: CubeQueryConfig | null,
  columnOrder?: string[],
  columnLabels?: string[],
): TableData {
  if (!cubeData || cubeData.length === 0) {
    return { columns: [], rows: [] };
  }

  const firstRow = cubeData[0];

  // If no query config (SQL mode), infer from data
  if (!query) {
    // Use columnOrder if provided, otherwise fall back to Object.keys
    const usingColumnOrder = !!columnOrder && columnOrder.length > 0;
    const allKeys = usingColumnOrder ? columnOrder : Object.keys(firstRow);

    const columns = allKeys.map((key, index) => {
      const value = firstRow[key];
      const isNumeric =
        typeof value === "number" ||
        (!isNaN(Number(value)) && value !== null && value !== "");
      // Format the label: remove "orders." prefix, then capitalize and replace underscores
      const cleanKey = key.replace(/^orders\./, "");
      // SQL alias (AS) for this column, if given - columnLabels is only
      // meaningful in lockstep with columnOrder, not the Object.keys fallback.
      const alias = usingColumnOrder ? columnLabels?.[index] : undefined;
      return {
        align: isNumeric ? ("right" as const) : ("left" as const),
        key,
        label:
          alias ||
          cleanKey.charAt(0).toUpperCase() +
            cleanKey.slice(1).replace(/_/g, " "),
        type: isNumeric ? ("number" as const) : ("string" as const),
      };
    });

    // Convert cubeData to ensure no null values and format dates
    const rows = cubeData.map((row) => {
      const cleanRow: Record<string, number | string> = {};
      allKeys.forEach((key, index) => {
        const rawValue = row[key];
        if (rawValue === null || rawValue === undefined || rawValue === "") {
          cleanRow[key] =
            columns[index].type === "number" ? 0 : missingLabelFallback(key);
          return;
        }
        if (columns[index].type === "number") {
          cleanRow[key] = Number(rawValue);
          return;
        }
        cleanRow[key] =
          typeof rawValue === "string" && isISODate(rawValue)
            ? formatDateLabel(rawValue)
            : rawValue;
      });
      return cleanRow;
    });

    return { columns, rows };
  }

  // Cube.js mode
  // Get all dimension keys including time dimensions
  // Only include time dimensions that have a granularity (used for grouping)
  // Time dimensions without granularity are used only for filtering
  const timeDimensionKeys = query.timeDimensions
    ? query.timeDimensions
        .filter((td) => td.granularity) // Only include if used for grouping
        .map((td) => td.dimension)
    : [];
  const regularDimensionKeys = query.dimensions || [];
  const dimensionKeys = [...timeDimensionKeys, ...regularDimensionKeys];
  const measureKeys = query.measures || [];
  const allKeys = [...dimensionKeys, ...measureKeys];

  // Safety check: if no keys from query, infer from data
  if (allKeys.length === 0) {
    const inferredKeys = Object.keys(firstRow);
    const columns = inferredKeys.map((key) => {
      // Try to detect if it's numeric by checking the value
      const value = firstRow[key];
      const isNumeric = !isNaN(Number(value)) && value !== null && value !== "";

      return {
        align: isNumeric ? ("right" as const) : ("left" as const),
        key,
        label:
          key.split(".").pop()?.charAt(0).toUpperCase() +
          (key.split(".").pop()?.slice(1).replace(/_/g, " ") || key),
        type: isNumeric ? ("number" as const) : ("string" as const),
      };
    });

    const rows = cubeData.map((row) => {
      const cleanRow: Record<string, number | string> = {};
      inferredKeys.forEach((key, index) => {
        const rawValue = row[key];
        if (rawValue === null || rawValue === undefined || rawValue === "") {
          cleanRow[key] =
            columns[index].type === "number" ? 0 : missingLabelFallback(key);
          return;
        }
        cleanRow[key] =
          columns[index].type === "number" ? Number(rawValue) : rawValue;
      });
      return cleanRow;
    });

    return { columns, rows };
  }

  // Create columns from dimensions and measures
  const columns = allKeys.map((key) => {
    const name = key.split(".").pop() || key;
    const isMeasure = measureKeys.includes(key);

    return {
      align: isMeasure ? ("right" as const) : ("left" as const),
      key,
      label: name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, " "),
      type: isMeasure ? ("number" as const) : ("string" as const),
    };
  });

  // Map rows and format dates
  const rows = cubeData.map((row) => {
    const mappedRow: Record<string, number | string> = {};
    allKeys.forEach((key, index) => {
      const rawValue = row[key];
      if (rawValue === null || rawValue === undefined || rawValue === "") {
        mappedRow[key] =
          columns[index].type === "number" ? 0 : missingLabelFallback(key);
        return;
      }
      if (columns[index].type === "number") {
        mappedRow[key] = Number(rawValue);
        return;
      }
      // Format dates for time dimensions, honoring the bucket granularity
      const timeDimension = query.timeDimensions?.find(
        (td) => td.dimension === key,
      );
      mappedRow[key] =
        timeDimension && typeof rawValue === "string" && isISODate(rawValue)
          ? formatDateLabel(rawValue, timeDimension.granularity)
          : rawValue;
    });
    return mappedRow;
  });

  return { columns, rows };
}

/**
 * Main transformation function - routes to appropriate transformer based on chart type
 * Supports both Cube.js and SQL query results
 */
export function transformCubeDataToChartFormat(
  cubeResponse: CubeQueryResponse,
  chartType: ChartType,
  query: CubeQueryConfig | null,
): ChartDataUnion {
  const cubeData = cubeResponse.data.map((row) => {
    const sanitized: CubeDataPoint = {};
    for (const [key, value] of Object.entries(row)) {
      sanitized[key] =
        value === "Pickup and Dropoff" ? "Point to Point" : value;
    }
    return sanitized;
  });
  const columnOrder = cubeResponse.columnOrder;
  const columnLabels = cubeResponse.columnLabels;

  switch (chartType) {
    case "metric":
      return transformToMetric(cubeData);

    case "gauge":
      return transformToGauge(cubeData);

    case "bar":
    case "column":
    case "line":
    case "area":
      return transformToBasicChart(cubeData, query, columnOrder, columnLabels);

    case "pie":
    case "donut":
      return transformToPieChart(cubeData, query, columnOrder, columnLabels);

    case "table":
      return transformToTable(cubeData, query, columnOrder, columnLabels);

    default:
      return transformToBasicChart(cubeData, query, columnOrder, columnLabels);
  }
}
